import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { previewInvitation, acceptInvitation } from '@/api/academy';
import { AlertCircle, Loader2, UserPlus } from 'lucide-react';
import './auth.css';

export function JoinPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<{ academy_id: string; email: string | null; role: string; expires_at: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ display_name: '', password: '' });

  useEffect(() => {
    if (!token) { setError('Missing invitation token'); setLoading(false); return; }
    previewInvitation(token)
      .then(inv => {
        setInvitation(inv);
        sessionStorage.setItem('academy_id', inv.academy_id);
        setLoading(false);
      })
      .catch(() => { setError('Invitation not found or expired'); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await acceptInvitation(token, form);
      localStorage.setItem('access_token', result.auth.access_token);
      localStorage.setItem('refresh_token', result.auth.refresh_token);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="auth-page">
      <div className="auth-card">
        <Loader2 size={32} className="auth-spin" />
      </div>
    </div>
  );

  if (error && !invitation) return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-card__error"><AlertCircle size={16} /><span>{error}</span></div>
      </div>
    </div>
  );

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-card__header">
          <h1 className="auth-card__brand">Join Academy</h1>
          <p className="auth-card__subtitle">
            You've been invited as <strong>{invitation?.role}</strong>
            {invitation?.email && <> for {invitation.email}</>}
          </p>
        </div>

        {error && (
          <div className="auth-card__error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-form__field">
            <label htmlFor="display_name">Your Name</label>
            <input
              id="display_name"
              type="text"
              value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              placeholder="Enter your name"
              required
              autoFocus
            />
          </div>
          <div className="auth-form__field">
            <label htmlFor="password">Create Password</label>
            <input
              id="password"
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Choose a password"
              required
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="auth-form__submit" disabled={submitting}>
            {submitting
              ? <><Loader2 size={18} className="auth-spin" /> Joining...</>
              : <><UserPlus size={18} /> Join Academy</>
            }
          </button>
        </form>
      </div>
    </div>
  );
}
